import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Reward } from '#src/components/3d/Reward/index.tsx';
import { CongratsScreen } from '#src/components/congratulations/CongratsScreen/index.tsx';
import { getCardRepresentation } from '#src/lib/cards.ts';
import { congratsScreens } from '#src/lib/glossary.ts';
import { lazy } from '#src/lib/retry.ts';
import { mergeRewards } from '#src/lib/rewards.tsx';

import { RewardTitle } from './RewardTitle/index.tsx';
import type {
  BuyConfirmation_anyCard,
  BuyConfirmation_anyReward,
} from './__generated__/index.graphql.ts';

const CardsWithRewards = lazy(
  async () => import('@sorare/trois/src/components/CardsWithRewards'),
  'CardsWithRewards'
);

type Props = {
  cards: BuyConfirmation_anyCard[];
  additionalRewards?: Nullable<BuyConfirmation_anyReward[]>;
  actions?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  onClose?: () => void;
};

export const BuyConfirmation = ({
  actions,
  cards,
  additionalRewards: additionalRewardsProp_,
  title,
  subtitle,
}: Props) => {
  const additionalRewardsProp = additionalRewardsProp_;
  const additionalRewards = useMemo(
    () => mergeRewards(additionalRewardsProp ?? []),
    [additionalRewardsProp]
  );
  const isBundle = cards.length > 1;
  const card = cards[0];

  const [nbRewardsRevealed, setNbRewardsRevealed] = useState(0);
  const nextReward = useCallback(() => {
    setNbRewardsRevealed(x => Math.min(x + 1, additionalRewards.length));
  }, [additionalRewards]);

  const cardsCount = cards.length;

  return (
    <CongratsScreen
      rarityBackground={card.rarityTyped}
      editionName={card.cardEditionName}
      title={
        additionalRewards.length > 0 && nbRewardsRevealed > 0 ? (
          <FormattedMessage
            id="buyTokenConfirmation.rewards.title"
            defaultMessage="There's{br}More"
            values={{ br: <br /> }}
          />
        ) : (
          title || (
            <FormattedMessage
              {...congratsScreens.newSigning}
              values={{ br: <br />, cardsCount }}
            />
          )
        )
      }
      subtitle={
        additionalRewards.length > 0 && nbRewardsRevealed > 0 ? (
          <RewardTitle reward={additionalRewards[nbRewardsRevealed - 1]} />
        ) : (
          subtitle || (
            <FormattedMessage
              {...(cardsCount === 1
                ? congratsScreens.welcomePlayerToYourTeam
                : congratsScreens.welcomeMultiplePlayersToYourTeam)}
              values={{
                playerDisplayName: card.anyPlayer.displayName,
                cardsCount,
              }}
            />
          )
        )
      }
      main={
        <Suspense fallback={<div style={{ height: '500px' }} />}>
          <CardsWithRewards
            cards={cards.map(c => getCardRepresentation(c))}
            rewards={additionalRewards
              .slice(0, nbRewardsRevealed)
              .map((reward, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Reward reward={reward} key={index} />
              ))}
            style={{
              height: '500px',
            }}
          />
        </Suspense>
      }
      actions={
        nbRewardsRevealed < additionalRewards.length ? (
          <Button size="medium" color="secondary" onClick={nextReward}>
            <FormattedMessage
              id="buyTokenConfirmation.buy.showRewards"
              defaultMessage="And..."
            />
          </Button>
        ) : (
          actions
        )
      }
      isBundle={isBundle}
    />
  );
};

BuyConfirmation.fragments = {
  anyCard: gql`
    fragment BuyConfirmation_anyCard on AnyCardInterface {
      slug
      sport
      cardEditionName
      rarityTyped
      anyPlayer {
        slug
        displayName
      }
      ...getCardRepresentation_anyCardInterface
    }
    ${getCardRepresentation.fragments.anyCardInterface}
  ` as TypedDocumentNode<BuyConfirmation_anyCard>,
  anyReward: gql`
    fragment BuyConfirmation_anyReward on AnyRewardInterface {
      id
      ...Reward_AnyRewardInterface
      ...RewardTitle_AnyRewardInterface
      ...mergeRewards_anyRewardInterface
    }
    ${Reward.fragments.AnyRewardInterface}
    ${RewardTitle.fragments.AnyRewardInterface}
    ${mergeRewards.fragments.AnyRewardInterface}
  ` as TypedDocumentNode<BuyConfirmation_anyReward>,
};
